import {
  AnimationEvent,
  transition,
  trigger,
  useAnimation,
} from '@angular/animations';
import { CdkPortal, DomPortalOutlet, Portal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ContentChild,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { slideIn, slideOut } from '@freelancer/animations';
import { TimeUtils } from '@freelancer/time-utils';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import { toNumber } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { filter } from 'rxjs/operators';
import { CalloutColor } from './callout-color';
import { CalloutContentComponent } from './callout-content.component';
import { CalloutPlacement } from './callout-placement';
import { CalloutSize } from './callout-size';
import { CalloutTriggerComponent } from './callout-trigger.component';

interface CalloutContentPosition {
  position: number;
  isInViewport: boolean;
}

@Component({
  selector: 'fl-callout',
  template: `
    <ng-content select="fl-callout-trigger"></ng-content>
    <ng-template cdk-portal>
      <div
        class="ContentContainer"
        @paneAnimation
        [@.disabled]="!isFullscreenMode"
        [attr.data-mobile-fullscreen]="mobileFullscreen"
        (@paneAnimation.done)="animationDone($event)"
      >
        <ng-content select="fl-callout-content"></ng-content>
      </div>
    </ng-template>
  `,
  styleUrls: ['./callout.component.scss'],
  animations: [
    trigger('paneAnimation', [
      transition(':enter', [useAnimation(slideIn)]),
      transition(':leave', [useAnimation(slideOut)]),
    ]),
  ],
})
export class CalloutComponent implements OnInit, OnDestroy {
  @Input() color: CalloutColor = CalloutColor.LIGHT;
  @Input() edgeToEdge = false;
  @Input() hideArrow = false;
  @Input() hideCloseButton = false;
  @Input() hover = false;
  @Input() placement: CalloutPlacement;
  @Input() size?: CalloutSize;
  @Input() mobileCloseButton = true;

  /** Use when content is scrollable for a better mobile experience. */
  @Input() mobileFullscreen = false;

  /**
   * Use when callout is hoverable and clicking on the trigger performs an action.
   * This allow mobile devices to trigger callout after pressing for 500ms.
   * It is important to note that callout will not be triggered on desktop with
   * mobile viewport as opening the dialog will break the flow of the trigger's
   * original action.
   */
  @Input() mobileLongPress = false;

  /**
   * Forces callout to open on tap/click. Useful when callout
   * trigger is a clickable element but is disabled. Use `mobileLongPress`
   * whenever possible.
   */
  @Input() mobileForceClick = false;

  /**
   * View header's title that shows up for mobileFullscreen
   */
  @Input() mobileHeaderTitle: string;

  @Output() calloutClose = new EventEmitter<void>();
  @Output() calloutOpen = new EventEmitter<void>();

  @ContentChild(CalloutTriggerComponent, { static: true })
  triggerComponent: CalloutTriggerComponent;
  @ContentChild(CalloutContentComponent, { static: true })
  contentComponent: CalloutContentComponent;
  @ViewChild(CdkPortal, { static: true }) portal: Portal<any>;

  private triggerComponentTrigger?: Rx.Subscription;
  private triggerComponentOpen?: Rx.Subscription;
  private triggerComponentClose?: Rx.Subscription;
  private contentComponentClose?: Rx.Subscription;
  private contentComponentBodyClick?: Rx.Subscription;
  private contentComponentBodyMouseenter?: Rx.Subscription;
  private contentComponentBodyMouseleave?: Rx.Subscription;
  private routeChangeSubscription?: Rx.Subscription;
  private calloutIsOpen = false;
  private calloutNeedsToBeClosed = true;
  private documentClickEvent: () => void;
  private windowResizeEvent: () => void;
  private host: DomPortalOutlet;

  private canRemoveOverflow = false;
  isFullscreenMode = false;

  constructor(
    private renderer: Renderer2,
    private componentFactoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector,
    private timeUtils: TimeUtils,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  // TODO: The way we handle states of when a callout needs to be
  // closed or opened during a hover transition from the trigger
  // to the callout content is pretty all over the place. When
  // somebody has time (and clarity to think) redo the logic again.
  // Ref: T37940.

  ngOnInit() {
    this.contentComponent.setOptions({
      color: this.color,
      edgeToEdge: this.edgeToEdge,
      hideArrow: this.hideArrow,
      hideCloseButton: this.hideCloseButton,
      hover: this.hover,
      mobileFullscreen: this.mobileFullscreen,
      mobileHeaderTitle: this.mobileHeaderTitle,
      size: this.size,
      mobileCloseButton: this.mobileCloseButton,
    });
    this.contentComponent.setPlacement(this.placement);

    this.triggerComponent.setOptions({
      hover: this.hover,
      mobileForceClick: this.mobileForceClick,
      mobileLongPress: this.mobileLongPress,
    });

    this.documentClickEvent = this.renderer.listen('document', 'click', () => {
      if (this.calloutNeedsToBeClosed) {
        this.closeCallout();
      }

      this.calloutNeedsToBeClosed = true;
    });

    this.windowResizeEvent = this.renderer.listen('window', 'resize', () => {
      if (this.calloutNeedsToBeClosed) {
        this.closeCallout();
      }

      this.calloutNeedsToBeClosed = true;
    });

    this.triggerComponentTrigger = this.triggerComponent.trigger.subscribe(
      () => {
        this.toggleCallout();
      },
    );

    if (this.hover) {
      this.triggerComponentOpen = this.triggerComponent.open.subscribe(() => {
        this.openCallout();
      });

      this.triggerComponentClose = this.triggerComponent.close.subscribe(() => {
        this.calloutNeedsToBeClosed = true;
        this.checkIfCalloutNeedsClosing();
      });
    }

    this.routeChangeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.close();
      });
  }

  open() {
    if (!this.checkIfTriggerIsInView()) {
      this.triggerComponent.containerRef.element.nativeElement.scrollIntoView();
    }
    this.openCallout();
  }

  close() {
    this.closeCallout();
  }

  toggle() {
    this.toggleCallout();
  }

  private checkIfTriggerIsInView() {
    const triggerBoundingRect = this.triggerComponent.getElementBoundingRect();
    return (
      triggerBoundingRect.top >= 0 &&
      triggerBoundingRect.left >= 0 &&
      triggerBoundingRect.right <=
        (window.innerWidth || document.documentElement.clientWidth) &&
      triggerBoundingRect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  private toggleCallout() {
    if (this.calloutIsOpen) {
      this.closeCallout();
    } else {
      this.openCallout();
    }
  }

  private openCallout() {
    this.isFullscreenMode = this.isMobileView() && this.mobileFullscreen;

    this.createCallout();
    this.setCalloutPosition();
    this.setCalloutZIndex();
    this.calloutIsOpen = true;
    this.calloutNeedsToBeClosed = false;
    this.contentComponentClose = this.contentComponent.close.subscribe(() =>
      this.closeCallout(),
    );
    this.contentComponentBodyClick = this.contentComponent.bodyClick.subscribe(
      () => {
        this.calloutNeedsToBeClosed = false;
      },
    );

    if (this.hover) {
      this.contentComponentBodyMouseenter = this.contentComponent.bodyMouseenter.subscribe(
        () => {
          this.calloutNeedsToBeClosed = false;
        },
      );
      this.contentComponentBodyMouseleave = this.contentComponent.bodyMouseleave.subscribe(
        () => {
          this.calloutNeedsToBeClosed = true;
          this.checkIfCalloutNeedsClosing();
        },
      );
    }

    this.togglePageScrollState(this.calloutIsOpen);
    this.calloutOpen.emit();
  }

  private checkIfCalloutNeedsClosing() {
    this.timeUtils.setTimeout(() => {
      if (this.calloutNeedsToBeClosed) {
        this.closeCallout();
      }
    }, 100);
  }

  private closeCallout() {
    if (this.calloutIsOpen) {
      if (this.isFullscreenMode) {
        // Detach first before disposing so that :leave animation will work
        this.host.detach();
      } else {
        this.host.dispose();
      }

      this.calloutIsOpen = false;
      this.calloutNeedsToBeClosed = true;
      if (this.contentComponentClose) {
        this.contentComponentClose.unsubscribe();
      }
      if (this.contentComponentBodyClick) {
        this.contentComponentBodyClick.unsubscribe();
      }

      if (this.hover) {
        if (this.contentComponentBodyMouseenter) {
          this.contentComponentBodyMouseenter.unsubscribe();
        }
        if (this.contentComponentBodyMouseleave) {
          this.contentComponentBodyMouseleave.unsubscribe();
        }
      }

      this.togglePageScrollState(this.calloutIsOpen);
      this.calloutClose.emit();
    }
  }

  animationDone(event: AnimationEvent) {
    if (event.toState === 'void') {
      this.host.dispose();
    }
  }

  private createCallout() {
    if (!this.host || !this.host.hasAttached()) {
      // TODO: This should definitely just be an Overlay implementation
      // coming soon in @angular/cdk. Would solve a lot of problems.
      // Ref: T37940
      const pane = this.renderer.createElement('div') as HTMLDivElement;
      // FIXME: Probably in a global class?
      // Ref: T37940
      pane.style.position = 'absolute';
      pane.style.top = '0';
      pane.style.right = '0';
      pane.style.left = '0';
      this.renderer.appendChild(this.document.body, pane);
      this.host = new DomPortalOutlet(
        pane,
        this.componentFactoryResolver,
        this.applicationRef,
        this.injector,
      );
      this.host.attach(this.portal);
    }
  }

  private setCalloutPosition() {
    const { position, placement } = this.calculatePosition(this.placement);

    this.contentComponent.setPosition(
      position.top,
      position.left,
      position.arrowTop,
      position.arrowLeft,
    );

    // Updates arrow placement when content is dynamically repositioned
    this.contentComponent.setPlacement(placement);
  }

  /**
   * Calculates the effective z-index of the trigger component,
   * then sets the z-index of the content to be just above.
   */
  private setCalloutZIndex() {
    // loop through the parent elements until we find one with a defined z-index
    // it's a bit gross but there's no easy one-call function to get applied z-index
    let currElement: HTMLElement = this.triggerComponent.containerRef.element
      .nativeElement;
    let currZIndex: string | null = window.getComputedStyle(currElement).zIndex;
    while (
      currElement.parentElement &&
      (!currZIndex || currZIndex === 'auto')
    ) {
      currElement = currElement.parentElement;
      currZIndex = window.getComputedStyle(currElement).zIndex;
    }
    // convert to a number if we found one, default to 0 otherwise
    const effectiveZIndex = toNumber(currZIndex) || 0;
    this.contentComponent.zIndex = effectiveZIndex + 11;
  }

  /**
   * Calculates all four possible callout content's `top` and `left`
   * properties relative to the callout trigger and checks if they
   * do not overflow in the current viewport / scroll position
   */
  private getContentPositions(params: {
    [key: string]: number;
  }): { [key: string]: CalloutContentPosition } {
    const {
      top,
      left,
      triggerHeight,
      triggerWidth,
      calloutHeight,
      calloutWidth,
      bodyHeight,
      bodyWidth,
      arrowOffset,
    } = params;

    const bottomPositioned = top + triggerHeight + arrowOffset;
    const topPositioned = top - calloutHeight - arrowOffset;
    const rightPositioned = left + triggerWidth + arrowOffset;
    const leftPositioned = left - calloutWidth - arrowOffset;

    return {
      bottom: {
        position: bottomPositioned, // `top` property
        isInViewport: bottomPositioned + calloutHeight <= bodyHeight,
      },
      top: {
        position: topPositioned, // `top` property
        isInViewport: topPositioned >= 0,
      },
      right: {
        position: rightPositioned, // `left` property
        isInViewport: rightPositioned + calloutWidth <= bodyWidth,
      },
      left: {
        position: leftPositioned, // `left` property
        isInViewport: leftPositioned >= 0,
      },
    };
  }

  private calculatePosition(
    placement: CalloutPlacement,
  ): {
    position: { [key: string]: number | undefined };
    placement: CalloutPlacement;
  } {
    // TODO: Yanked from
    // public/static/modules/@freelancer/ui-callout/index.js.
    // Should probably have some Angular rendering standards written
    // here or use @angular/cdk/overlay.
    // Ref: T37940;
    const arrowOffset = this.hideArrow ? 0 : 12;
    const triggerBoundingClientRect = this.triggerComponent.getElementBoundingRect();
    const triggerWidth = triggerBoundingClientRect.width;
    const triggerHeight = triggerBoundingClientRect.height;
    const calloutBoundingClientRect = this.contentComponent.getElementBoundingRect();
    const calloutWidth = calloutBoundingClientRect.width;
    const calloutHeight = calloutBoundingClientRect.height;

    // IE is such a bad browser. document.documentElement is used for IE
    // compatibility.
    const bodyTop =
      (document.documentElement && document.documentElement.scrollTop) ||
      document.body.scrollTop;
    const bodyLeft =
      (document.documentElement && document.documentElement.scrollLeft) ||
      document.body.scrollLeft;
    const bodyWidth = document.body.clientWidth;
    const bodyHeight = document.body.clientHeight;

    // Now calculate the positions to be set to the Callout element.
    // Find the location of the target element relative to the document first.
    let { top } = triggerBoundingClientRect;
    let left = triggerBoundingClientRect.left + bodyLeft;

    // Calculate all possible positions so we can reposition if it overflows
    const contentPositions = this.getContentPositions({
      top,
      left,
      triggerHeight,
      triggerWidth,
      calloutHeight,
      calloutWidth,
      bodyHeight,
      bodyWidth,
      arrowOffset,
    });

    if (
      placement === CalloutPlacement.BOTTOM ||
      placement === CalloutPlacement.BOTTOM_LEFT ||
      placement === CalloutPlacement.BOTTOM_RIGHT
    ) {
      // Recalculate the placement as a top placement if it overflows from
      // the bottom of the document. Disable this though if the new position
      // also overflows.
      if (
        !contentPositions.bottom.isInViewport &&
        contentPositions.top.isInViewport &&
        placement === this.placement
      ) {
        return this.calculatePosition(CalloutPlacement.TOP);
      }

      top = contentPositions.bottom.position + bodyTop;
    } else if (
      placement === CalloutPlacement.TOP ||
      placement === CalloutPlacement.TOP_LEFT ||
      placement === CalloutPlacement.TOP_RIGHT
    ) {
      // Recalculate the placement as a bottom placement if it overflows from
      // the top of the document. Disable this though if the new position
      // also overflows.
      if (
        !contentPositions.top.isInViewport &&
        contentPositions.bottom.isInViewport &&
        placement === this.placement
      ) {
        return this.calculatePosition(CalloutPlacement.BOTTOM);
      }

      top = contentPositions.top.position + bodyTop;
    } else {
      // This calculation works for CalloutPlacement.LEFT and CalloutPlacement.RIGHT.
      top =
        Math.max(
          Math.min(
            top + triggerHeight / 2 - calloutHeight / 2,
            bodyHeight - calloutHeight - arrowOffset,
          ),
          arrowOffset,
        ) + bodyTop;
    }

    if (placement === CalloutPlacement.LEFT) {
      // Recalculate the placement as a bottom placement if it overflows from
      // the left of the document. Disable this though if the new position
      // also overflows.
      if (
        !contentPositions.left.isInViewport &&
        contentPositions.bottom.isInViewport &&
        placement === this.placement
      ) {
        return this.calculatePosition(CalloutPlacement.BOTTOM);
      }

      left = contentPositions.left.position;
    } else if (placement === CalloutPlacement.RIGHT) {
      // Recalculate the placement as a bottom placement if it overflows from
      // the right of the document. Disable this though if the new position
      // also overflows.
      if (
        !contentPositions.right.isInViewport &&
        contentPositions.bottom.isInViewport &&
        placement === this.placement
      ) {
        return this.calculatePosition(CalloutPlacement.BOTTOM);
      }

      left = contentPositions.right.position;
    } else if (
      placement === CalloutPlacement.BOTTOM_LEFT ||
      placement === CalloutPlacement.TOP_LEFT
    ) {
      // If the callout width is less than 500 change to CalloutPlacement.BOTTOM
      // instead.
      if (calloutWidth < 500) {
        // TODO: This code still doesn't work properly because now the placement
        // jumps between BOTTOM to BOTTOM_LEFT on every viewport resize.
        // return this.calculatePosition(CalloutPlacement.BOTTOM);
      }
    } else if (
      placement === CalloutPlacement.BOTTOM_RIGHT ||
      placement === CalloutPlacement.TOP_RIGHT
    ) {
      // Set the callout at the right side of the target element.
      left = Math.min(
        // Between setting it in the right side of the target element or setting
        // it at the end of the page
        left + triggerWidth - calloutWidth,
        bodyWidth - calloutWidth - arrowOffset,
      );
    } else {
      // This calculation works for CalloutPlacement.TOP and CalloutPlacement.BOTTOM.
      left = Math.max(
        Math.min(
          // Between setting it in the middle of the target element or setting
          // it at the end of the page
          left + triggerWidth / 2 - calloutWidth / 2,
          bodyWidth - calloutWidth - arrowOffset,
        ),
        arrowOffset,
      );
    }

    // Now calculate the arrow position
    // Note that [x]_LEFT and [x]_RIGHT arrow positions are predefined
    // in CSS instead.
    let arrowTop;
    let arrowLeft;

    if (
      placement === CalloutPlacement.TOP ||
      placement === CalloutPlacement.BOTTOM
    ) {
      arrowLeft =
        triggerBoundingClientRect.left + bodyLeft + triggerWidth / 2 - left;
    } else if (
      placement === CalloutPlacement.LEFT ||
      placement === CalloutPlacement.RIGHT
    ) {
      arrowTop =
        triggerBoundingClientRect.top + bodyTop + triggerHeight / 2 - top;
    }

    return {
      position: {
        top,
        left,
        arrowTop,
        arrowLeft,
      },
      placement,
    };
  }

  /**
   * Prevents body from scrolling on mobile since
   * it becomes a dialog with overlay
   */
  togglePageScrollState(isCalloutOpen: boolean) {
    const target = this.document.body;
    const currentOverflow = target.style.overflow;

    // We only want to remove overflow when callout component is the
    // one that set it. For instance, when callout is placed within a modal
    // overflow is already set and we don't want to remove it
    // once callout is closed
    if (isCalloutOpen && !currentOverflow) {
      this.canRemoveOverflow = true;
    }

    if (isCalloutOpen && this.isMobileView()) {
      this.renderer.setStyle(target, 'overflow', 'hidden');
    } else if (!isCalloutOpen && this.canRemoveOverflow) {
      this.renderer.removeStyle(target, 'overflow');
      this.canRemoveOverflow = false;
    }
  }

  private isMobileView() {
    return window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
  }

  ngOnDestroy() {
    this.closeCallout();

    if (this.documentClickEvent) {
      this.documentClickEvent();
    }

    if (this.windowResizeEvent) {
      this.windowResizeEvent();
    }

    if (this.triggerComponentTrigger) {
      this.triggerComponentTrigger.unsubscribe();
    }

    if (this.hover) {
      if (this.triggerComponentOpen) {
        this.triggerComponentOpen.unsubscribe();
      }
      if (this.triggerComponentClose) {
        this.triggerComponentClose.unsubscribe();
      }
    }

    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
    }
  }
}
